// The cost-discipline rules, written down as data (spec/19 §7, spec/21 §6).
//
// These are build requirements, not advice. At 50,000 stations a log line per heartbeat is a
// five-figure monthly mistake, and the mistake is not made once — it is made by the next person who
// adds a `logger.info` to a keepalive path. So the rule lives here, in the package that imports
// nothing, and three things read it:
//
//   the server        `apps/server/src/observability/cost.ts` refuses the line and drops the span
//   the collector     `infra/observability/` renders the filter and tail-sampling processors from
//                     this list, so the second line of defence is the same list as the first
//   the tests         `apps/server/src/observability/__tests__/cost-discipline.test.ts` floods
//                     heartbeats and proves nothing was written
//
// A name that is not in `SILENT_OPERATIONS` is logged. That is the right way round: adding a new
// keepalive means adding it here, deliberately, in a diff someone reads.
import { z } from "../zod.js";
/**
 * The operations that must never produce a log line, and whose spans are dropped before they are
 * recorded (spec/19 §7 rule 1 and rule 5).
 *
 * Two kinds, and they are here for the same reason:
 *
 *   the health probes     every pod, every few seconds, for ever. Kubernetes liveness and readiness
 *                         traffic is the largest request count in a healthy cluster and carries no
 *                         information at all — a failing `/readyz` is visible as a pod leaving the
 *                         load balancer and as `clavian.readiness.failures`, not as a log line.
 *   the keepalives        `station.heartbeat` and the protocol's ping/pong. 50,000 stations at one
 *                         refresh every 30 s is 144 million messages a month. Presence is a hash
 *                         write and a TTL in Valkey; Postgres sees only a state change
 *                         (spec/03 §7.3, spec/09 §8).
 */
export const SILENT_OPERATIONS = [
    'GET /healthz',
    'GET /readyz',
    'station.heartbeat',
    'realtime.ping',
    'realtime.pong',
];
/**
 * The HTTP paths behind the silent operations above, for the middleware that has a path before it
 * has matched a route, and for the collector, which only ever sees `http.route`.
 */
export const SILENT_HTTP_PATHS = ['/healthz', '/readyz'];
const SILENT = new Set(SILENT_OPERATIONS);
const SILENT_PATHS = new Set(SILENT_HTTP_PATHS);
/** Is this operation one that must never be logged or traced? */
export function isSilentOperation(operation) {
    return operation !== undefined && SILENT.has(operation);
}
/** Is this request path a health probe? Exact match: `/healthz/../v1/orders` is not a probe. */
export function isSilentHttpPath(path) {
    return path !== undefined && SILENT_PATHS.has(path);
}
/** The operation name a request is known by, so one string keys the log, the span and the metric. */
export function httpOperation(method, route) {
    return `${method.toUpperCase()} ${route}`;
}
// ---------------------------------------------------------------------------------------------
// Retention
// ---------------------------------------------------------------------------------------------
/**
 * How long each signal is kept, and why (spec/19 §7 rule 5, "cap log retention").
 *
 * Traces take the number the specification already sets for telemetry and realtime traces
 * (spec/04 §13, spec/20 §8). Logs and metrics are not given a number anywhere in the
 * specification, so these two are decisions:
 *
 *   logs     30 days. Long enough for a dispute about last month's session, short enough that the
 *            log store is not the largest line on the bill. What has to outlive it is not a log:
 *            money is the ledger, access is the audit log (7 years, spec/20 §7).
 *   metrics  13 months. Pre-aggregated and cheap, and 13 rather than 12 so this December can be
 *            read against last December — the SLO report and the capacity plan both want it.
 */
export const TELEMETRY_RETENTION = {
    logs: {
        days: 30,
        why: 'A cap, per spec/19 §7 rule 5. Anything that must outlive it is an audit row or a ledger line, not a log.',
        spec: null,
    },
    traces: {
        days: 90,
        why: 'The number the specification already gives for telemetry and realtime traces.',
        spec: 'spec/04-domain-model.md §13',
    },
    metrics: {
        days: 395,
        why: 'Thirteen months, so this December reads against last December for the SLO report and the capacity plan.',
        spec: null,
    },
};
// ---------------------------------------------------------------------------------------------
// Sampling
// ---------------------------------------------------------------------------------------------
/**
 * The head-sampling ratio per environment (spec/19 §7 rule 5, "sample traces").
 *
 * `local` and `preview` keep everything, because the whole point of a trace on a developer's
 * machine is that the one request they just made is in it. `staging` keeps a quarter.
 * `production` keeps 5% of the traffic that nothing else has an opinion about — and the sampler
 * is not the only opinion: a span that records an error is kept whatever the ratio says
 * (`apps/server/src/observability/sampling.ts`), and the collector's tail sampler keeps a trace
 * that turns out to contain an error or to be slow.
 */
export const TRACE_SAMPLE_RATIOS = {
    local: 1,
    preview: 1,
    staging: 0.25,
    production: 0.05,
};
export const TraceSampleRatioSchema = z
    .number()
    .min(0, 'A sampling ratio is between 0 and 1.')
    .max(1, 'A sampling ratio is between 0 and 1.');
//# sourceMappingURL=cost.js.map